import React, { forwardRef } from 'react';

import { useLoadScript } from '@react-google-maps/api';
import Geocode from 'react-geocode';
import { geocodeByAddress } from 'react-places-autocomplete';

import { MuiLocationInput } from '@shared/ui-kit';

import type { Libraries } from '@react-google-maps/api';
import type { Nullable } from '@shared/types/base-custom.type';

Geocode.setApiKey('AIzaSyD36amaFsc13wDeoSCnWW89FI2OY2pZtVU');
Geocode.setLanguage('en');
const libraries: Libraries = ['drawing', 'places', 'visualization', 'core'];

type PropsType = {
  label?: string;
  isError?: boolean;
  errorMessage?: string;
  [x: string]: any;
  name: string;
  placeholder?: string;
  inputVariant?: 'outline' | 'text';
  isRequired?: boolean;

  handleSelectAddress?: (autofillData: AutofillGeocodingDataFromAddress) => void;

  value?: { address?: string; map?: { lat?: number; lng?: number } };
  onChange: (e: { address?: string; map?: { lat?: number; lng?: number } }) => void;
};

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export type AutofillGeocodingDataFromAddress = {
  lat: Nullable<number>;
  lng: Nullable<number>;
  country: Nullable<string>;
  state: Nullable<string>;
  city: Nullable<string>;
  zip: Nullable<string>;
  street: Nullable<string>;
  number: Nullable<string>;
};

const extractNumbersFromAddress = (address: string) => {
  const match = address.match(/^\d+/);
  return match ? parseInt(match[0], 10) : null;
};
async function getGeocodingDataFromAddress(address: string) {
  const maxTries = 2;
  const key = 'AIzaSyCFDzfyH7KIIUvgueWOD86mHPxt8uLlQ_c';
  const countryCode = 'en';

  const returnData: AutofillGeocodingDataFromAddress = {
    lat: null,
    lng: null,
    country: null,
    state: null,
    city: null,
    zip: null,
    street: null,
    number: null,
  };

  // If the address is empty, return empty data
  if (address === '') {
    return returnData;
  }

  // Construct URL for the Google Geocoding API request
  const geocodingUrl =
    `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}` +
    `&key=${key}&language=${countryCode}`;

  let status = '';
  let geocodingResponse = [];
  let tries = 0;

  // Repeat the request until a response with 'OK' status is received or the maximum number of tries is reached
  while (status !== 'OK' && tries <= maxTries) {
    if (tries >= 1) {
      await sleep(1000); // Пауза 1 секунда между повторными запросами
    }
    const response = await fetch(geocodingUrl);
    geocodingResponse = await response.json();
    status = geocodingResponse.status;
    tries++;
  }

  // If the response is empty, return empty data
  if (!geocodingResponse.results || geocodingResponse.results.length === 0) {
    return returnData;
  }

  const addressNumber = extractNumbersFromAddress(address);

  // Process the geocoding results
  for (const result of geocodingResponse.results) {
    if (result.geometry.location.lat && returnData.lat === null) {
      returnData.lat = result.geometry.location.lat;
    }
    if (result.geometry.location.lng && returnData.lng === null) {
      returnData.lng = result.geometry.location.lng;
    }

    if (result.address_components && result.address_components.length > 0) {
      for (const addressComponent of result.address_components) {
        switch (addressComponent.types[0]) {
          case 'country':
            returnData.country = addressComponent.long_name;
            break;
          case 'administrative_area_level_1':
            returnData.state = addressComponent.short_name;
            break;
          case 'locality':
            returnData.city = addressComponent.long_name;
            break;
          case 'postal_code':
            returnData.zip = addressComponent.short_name;
            break;
          case 'route':
            returnData.street = addressNumber
              ? `${addressNumber}, ${addressComponent.long_name}`
              : addressComponent.long_name;
            break;
          case 'street_number':
            returnData.number = addressComponent.short_name;
            break;
        }
      }
    }
  }

  // If the city is not empty and postal code is empty, perform a reverse geocoding request to fetch the postal code using coordinates
  if (
    returnData.lat !== null &&
    returnData.lng !== null &&
    returnData.city !== null &&
    returnData.zip === null
  ) {
    const reverseGeocodingUrl =
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${encodeURIComponent(
        returnData.lat + ',' + returnData.lng,
      )}` + `&key=${key}&language=${countryCode}`;

    status = '';
    geocodingResponse = [];
    tries = 0;

    while (status !== 'OK' && tries <= maxTries) {
      if (tries >= 1) {
        await sleep(1000);
      }
      const reverseResponse = await fetch(reverseGeocodingUrl);
      geocodingResponse = await reverseResponse.json();
      status = geocodingResponse.status;
      tries++;
    }

    for (const result of geocodingResponse.results) {
      if (result.address_components && result.address_components.length > 0) {
        for (const addressComponent of result.address_components) {
          if (addressComponent.types[0] === 'postal_code' && returnData.zip === null) {
            returnData.zip = addressComponent.short_name;
          }
        }
      }
    }
  }
  return returnData;
}

export const MuiLocationAutofillByAddress = forwardRef<HTMLHeadingElement, PropsType>(
  (
    {
      placeholder,
      value,
      onChange,
      name,
      isError,
      errorMessage,
      label,
      inputVariant,
      handleSelectAddress,
      isRequired,
      ...args
    },
    ref,
  ) => {
    const { isLoaded } = useLoadScript({
      googleMapsApiKey: 'AIzaSyCFDzfyH7KIIUvgueWOD86mHPxt8uLlQ_c',
      libraries,
      language: 'en',
    });

    const onSearchError = () => {
      // onChange({ address: '', map: {} });
    };
    const handleChangeInput = (e: string) => {
      if (e?.length === 0) {
        onChange({ address: e, map: {} });
      } else {
        onChange({ ...value, address: e });
      }
    };

    const handleSelect = async (address: string) => {
      const autofillResult = await getGeocodingDataFromAddress(address);

      geocodeByAddress(address).then(async (response) => {
        const reformatAddress = `${address}, ${autofillResult.zip || ''}`;

        if (
          response &&
          response?.length === 1 &&
          response[0] &&
          response[0]?.geometry &&
          response[0]?.geometry?.location
        ) {
          onChange({
            address: reformatAddress,
            map: {
              lat: response[0]?.geometry?.location.lat(),
              lng: response[0]?.geometry?.location.lng(),
            },
          });
          handleSelectAddress?.({ ...autofillResult, street: reformatAddress });
        } else {
          onChange({ address: '', map: {} });
          handleSelectAddress?.(autofillResult);
        }
      });
    };

    return isLoaded ? (
      <MuiLocationInput
        {...args}
        value={value?.address || ''}
        name={name}
        inputVariant={inputVariant}
        onChange={(e) => handleChangeInput(e)}
        handleSelect={handleSelect}
        onSearchError={onSearchError}
        placeholder={placeholder}
        label={label}
        isRequired={isRequired}
        isError={isError}
        errorMessage={errorMessage}
        ref={ref}
      />
    ) : (
      <></>
    );
  },
);
